import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import MobileCTA from '@/components/MobileCTA';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, Phone, Clock, Mail, MessageSquare, Send } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';
import { trackContactFormSubmit, trackPhoneClick, trackEmailClick, trackMapClick } from '@/lib/analytics';

// Zod validation schema for contact form input validation
const contactFormSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Invalid email format").max(255, "Email must be less than 255 characters"),
  phone: z.string().trim().max(20, "Phone number must be less than 20 characters").optional(),
  subject: z.string().trim().max(200, "Subject must be less than 200 characters").optional(),
  message: z.string().trim().min(1, "Message is required").max(2000, "Message must be less than 2000 characters")
});

const ContactPage = () => {
  const { language, t } = useLanguage();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate form data before database insertion
      const validatedData = contactFormSchema.parse({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        subject: formData.subject || undefined,
        message: formData.message
      });

      // Save to database with validated data
      const { error: dbError } = await supabase
        .from('leads')
        .insert({
          name: validatedData.name,
          email: validatedData.email,
          phone: validatedData.phone || null,
          subject: validatedData.subject || null,
          message: validatedData.message,
          preferred_language: language,
          source_page: 'contact'
        });

      if (dbError) throw dbError;

      // Send emails
      const { error: emailError } = await supabase.functions.invoke('send-contact-email', {
        body: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          language: language
        }
      });

      if (emailError) {
        console.error('Email sending failed:', emailError);
        // Don't throw error here - form was saved successfully
      }

      trackContactFormSubmit({ subject: validatedData.subject || 'general', language, source_page: 'contact' });

      toast({
        title: language === 'en' ? "Message Sent!" : "¡Mensaje Enviado!",
        description: language === 'en' 
          ? "Thank you for contacting us. We'll get back to you soon!"
          : "Gracias por contactarnos. ¡Nos pondremos en contacto pronto!"
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      
      if (error instanceof z.ZodError) {
        toast({
          title: language === 'en' ? "Validation Error" : "Error de Validación",
          description: error.errors[0].message,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Error",
          description: language === 'en' 
            ? "Failed to send message. Please try again."
            : "Error al enviar mensaje. Por favor intente de nuevo.",
          variant: "destructive"
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // SEO structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": "The Grass Depot LLC",
      "telephone": "(936) 320-0763",
      "email": "thegrassdepotllc@gmail.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "6920 FM 1488 RD",
        "addressLocality": "Magnolia",
        "addressRegion": "TX",
        "postalCode": "77354",
        "addressCountry": "US"
      },
      "openingHours": ["Mo-Fr 07:00-17:00", "Sa 08:00-16:00"],
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "30.1588",
        "longitude": "-95.4613"
      }
    }
  };

  const metaTitle = language === 'en' 
    ? 'Contact The Grass Depot | Free Landscaping Quotes Texas'
    : 'Contactar The Grass Depot | Cotizaciones Gratis Paisajismo Texas';

  const metaDescription = language === 'en'
    ? 'Contact The Grass Depot for free landscaping quotes in Montgomery County, Texas. Professional sod installation, bulk material delivery, and landscape design. Call (936) 320-0763 now!'
    : 'Contacta The Grass Depot para cotizaciones gratis de paisajismo en el Condado de Montgomery, Texas. Instalación profesional de césped, entrega de materiales a granel y diseño de paisajes. ¡Llama (936) 320-0763 ahora!';

  const seoKeywords = language === 'en'
    ? 'contact landscaping company Texas, free landscaping quote Montgomery County, sod installation contact, landscape design consultation, Texas landscaping services contact'
    : 'contactar empresa paisajismo Texas, cotización paisajismo gratis Condado Montgomery, contacto instalación césped, consulta diseño paisajes, contacto servicios paisajismo Texas';

  const breadcrumbs = [
    { name: language === 'en' ? 'Home' : 'Inicio', url: `/${language}` },
    { name: language === 'en' ? 'Contact' : 'Contacto', url: `/${language}/contact` }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={metaTitle}
        description={metaDescription}
        keywords={seoKeywords}
        canonical={`/${language}/contact`}
        structuredData={structuredData}
        language={language}
        breadcrumbs={breadcrumbs}
      />
      <Header />
      <MobileCTA />
      
      <main>
      {/* Hero Section */}
      <section className="relative bg-gradient-hero py-20 px-4 text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-secondary to-accent opacity-90"></div>
        <div className="container mx-auto text-center relative z-10">
          <div className="flex items-center justify-center mb-6 animate-fade-in">
            <MessageSquare className="h-12 w-12 text-white mr-4" />
            <h1 className="text-4xl md:text-6xl font-bold text-white">
              {language === 'en' ? 'Contact Us' : 'Contáctenos'}
            </h1>
          </div>
          <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto animate-fade-in">
            {language === 'en' 
              ? 'Get in touch for premium landscaping materials delivery throughout Montgomery County, Texas.'
              : 'Póngase en contacto para la entrega de materiales de jardinería premium en todo el Condado Montgomery, Texas.'
            }
          </p>
          <div className="flex flex-wrap justify-center gap-4 animate-scale-in">
            <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2 text-base">
              <Clock className="h-5 w-5 mr-2" />
              {language === 'en' ? '24/7 Online Quotes' : 'Cotizaciones Online 24/7'}
            </Badge>
            <Badge variant="secondary" className="bg-white/20 text-white border-white/30 px-4 py-2 text-base">
              <Phone className="h-5 w-5 mr-2" />
              {language === 'en' ? 'Montgomery County Delivery' : 'Entrega Condado Montgomery'}
            </Badge>
          </div>
        </div>
      </section>

      {/* Contact Information & Form */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-2 gap-12">
            
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-foreground mb-6">
                  {language === 'en' ? 'Get In Touch' : 'Póngase en Contacto'}
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  {language === 'en'
                    ? 'Ready to transform your grass or rock project? Contact us today for professional delivery of premium sod, rock, mulch, soil, and sand throughout Montgomery County.'
                    : '¿Listo para transformar su proyecto de césped o roca? Contáctenos hoy para entrega profesional de pasto, roca, mulch, tierra y arena premium en todo el Condado Montgomery.'
                  }
                </p>
              </div>

              {/* Contact Cards */}
              <div className="space-y-4">
                <Card className="border-l-4 border-l-primary">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-primary/10 rounded-full p-3">
                        <Phone className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-foreground mb-1">
                          {language === 'en' ? 'Call Us' : 'Llámenos'}
                        </h3>
                        <a 
                          href="tel:9363200763" 
                          className="text-xl font-bold text-primary hover:text-primary-dark transition-colors"
                          data-event-name="phone_click"
                          data-source="contact_page"
                          onClick={() => trackPhoneClick('contact_page')}
                        >
                          (936) 320-0763
                        </a>
                        <p className="text-sm text-muted-foreground mt-1">
                          {language === 'en' ? 'Mon-Fri: 7:30AM-4:30PM, Sat: 8AM-12:30PM, Sun: Closed' : 'Lun-Vie: 7:30AM-4:30PM, Sáb: 8AM-12:30PM, Dom: Cerrado'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-primary">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-primary/10 rounded-full p-3">
                        <MapPin className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-foreground mb-1">
                          {language === 'en' ? 'Visit Us' : 'Visítenos'}
                        </h3>
                        <p className="text-foreground font-medium">
                          6920 FM 1488 RD<br />
                          Magnolia, TX 77354
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {language === 'en' ? 'Pickup available by appointment' : 'Recogida disponible con cita'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-primary">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-primary/10 rounded-full p-3">
                        <Mail className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-foreground mb-1">
                          {language === 'en' ? 'Email Us' : 'Envíenos un Email'}
                        </h3>
                        <a 
                          href="mailto:thegrassdepotllc@gmail.com" 
                          className="text-primary hover:text-primary-dark transition-colors"
                          data-event-name="email_click"
                          data-source="contact_page"
                          onClick={() => trackEmailClick('contact_page')}
                        >
                          thegrassdepotllc@gmail.com
                        </a>
                        <p className="text-sm text-muted-foreground mt-1">
                          {language === 'en' ? 'We respond within 24 hours' : 'Respondemos dentro de 24 horas'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Service Areas */}
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-4">
                  {language === 'en' ? 'Service Areas' : 'Áreas de Servicio'}
                </h3>
                <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                  <span>Magnolia TX</span>
                  <span>The Woodlands TX</span>
                  <span>Conroe TX</span>
                  <span>Cypress TX</span>
                  <span>Tomball TX</span>
                  <span>Spring TX</span>
                  <span>Willis TX</span>
                  <span>{language === 'en' ? 'Montgomery County TX' : 'Condado Montgomery TX'}</span>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl">
                  {language === 'en' ? 'Send us a Message' : 'Envíenos un Mensaje'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        {language === 'en' ? 'Name *' : 'Nombre *'}
                      </label>
                      <Input
                        required
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        placeholder={language === 'en' ? 'Your full name' : 'Su nombre completo'}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">
                        {language === 'en' ? 'Email *' : 'Email *'}
                      </label>
                      <Input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        placeholder={language === 'en' ? 'your@email.com' : 'su@email.com'}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {language === 'en' ? 'Phone' : 'Teléfono'}
                    </label>
                    <Input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      placeholder="(936) 320-0763"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {language === 'en' ? 'Subject' : 'Asunto'}
                    </label>
                    <Select value={formData.subject} onValueChange={(value) => handleChange('subject', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder={language === 'en' ? 'Select a subject' : 'Seleccionar un asunto'} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="quote">
                          {language === 'en' ? 'Request Quote' : 'Solicitar Cotización'}
                        </SelectItem>
                        <SelectItem value="delivery">
                          {language === 'en' ? 'Delivery Information' : 'Información de Entrega'}
                        </SelectItem>
                        <SelectItem value="products">
                          {language === 'en' ? 'Product Questions' : 'Preguntas sobre Productos'}
                        </SelectItem>
                        <SelectItem value="support">
                          {language === 'en' ? 'Customer Support' : 'Soporte al Cliente'}
                        </SelectItem>
                        <SelectItem value="other">
                          {language === 'en' ? 'Other' : 'Otro'}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {language === 'en' ? 'Message *' : 'Mensaje *'}
                    </label>
                    <Textarea
                      required
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      placeholder={language === 'en' 
                        ? 'Tell us about your landscaping needs...'
                        : 'Cuéntenos sobre sus necesidades de jardinería...'
                      }
                      rows={5}
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={loading}
                    variant="hero"
                  >
                    {loading ? (
                      language === 'en' ? 'Sending...' : 'Enviando...'
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        {language === 'en' ? 'Send Message' : 'Enviar Mensaje'}
                      </>
                    )}
                  </Button>
                </form>
                
                {/* Storefront Image */}
                <div className="mt-6 text-center">
                  <img 
                    src="/grass-depot-storefront.png" 
                    alt={language === 'en' ? 'The Grass Depot Storefront in Magnolia, Texas' : 'Fachada de The Grass Depot en Magnolia, Texas'}
                    className="w-full h-auto rounded-lg shadow-md"
                  />
                  <p className="text-sm text-muted-foreground mt-2">
                    {language === 'en' ? 'Visit our store in Magnolia, Texas' : 'Visite nuestra tienda en Magnolia, Texas'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 px-4 bg-muted">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold text-foreground mb-8">
            {language === 'en' ? 'Find Us' : 'Encuéntranos'}
          </h2>
          <div
            className="bg-background rounded-lg p-4 max-w-4xl mx-auto"
            data-event-name="map_click"
            data-source="contact_map"
            onMouseDown={() => trackMapClick('contact_map')}
            onTouchStart={() => trackMapClick('contact_map')}
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3437.123456789!2d-95.1234567!3d30.1234567!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s6920+FM+1488+RD%2C+Magnolia%2C+TX+77354!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="rounded-lg"
              title={language === 'en' ? 'Map to The Grass Depot LLC in Magnolia, TX' : 'Mapa a The Grass Depot LLC en Magnolia, TX'}
            ></iframe>
          </div>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;